import { CreditCard, LoaderCircle, ShieldCheck, TriangleAlert } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import useSWR from "swr";
import { swrFetcher } from "@/web/api";
import brandLogo from "@/web/assets/brand-logo.png";
import { Button } from "@/web/components/ui/button";
import { Input } from "@/web/components/ui/input";
import { Label } from "@/web/components/ui/label";
import { Loading } from "@/web/components/ui/loading";
import { BRAND_NAME, BRAND_TAGLINE } from "@/web/lib/brand";
import { cn } from "@/web/lib/utils";

interface AggregateConfig {
  enabled: boolean;
  methods: string[];
}

const METHOD_LABEL: Record<string, string> = { alipay: "支付宝", wxpay: "微信支付" };

/**
 * 聚合支付页（/pay）。
 *
 * 对外公开：访客自己填金额、选支付方式，提交后跳到收银台。
 * 这里不用 apiFetch —— 它是给后台用的（会带会话/CSRF 与 401 跳登录），
 * 公开页面直接用原生 fetch 更干净。
 */
export function AggregatePayPage() {
  const { data, isLoading } = useSWR<AggregateConfig>("/public-api/aggregate/config", swrFetcher, { shouldRetryOnError: false });
  const [money, setMoney] = useState("");
  const [remark, setRemark] = useState("");
  const [type, setType] = useState("alipay");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const methods = data?.methods ?? [];
  const methodKey = methods.join(",");

  useEffect(() => {
    const first = methods[0];
    if (first && !methods.includes(type)) setType(first);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methodKey, type]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const response = await fetch("/public-api/aggregate/order", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ money, name: remark, type }),
      });
      const result = (await response.json().catch(() => null)) as
        { code?: number; msg?: string; checkout_url?: string } | null;
      if (!result || result.code !== 1 || !result.checkout_url) {
        throw new Error(result?.msg || "下单失败，请稍后再试");
      }
      window.location.href = result.checkout_url;
    } catch (err) {
      setError(err instanceof Error ? err.message : "下单失败");
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <header className="flex items-center justify-center gap-4">
          <img src={brandLogo} alt="" className="h-14 w-14 shrink-0 object-contain" />
          <span className="flex flex-col leading-tight">
            <span className="text-[19px] font-semibold tracking-tight">{BRAND_NAME}</span>
            <span className="text-[13px] text-muted">{BRAND_TAGLINE}</span>
          </span>
        </header>

        {isLoading ? (
          <div className="mt-8">
            <Loading label="正在加载" />
          </div>
        ) : !data?.enabled ? (
          <div className="mt-8 rounded-2xl border p-8 text-center">
            <TriangleAlert className="mx-auto size-8 text-destructive" />
            <h1 className="mt-4 text-lg font-semibold tracking-tight">聚合支付未开启</h1>
            <p className="mt-2 text-sm leading-6 text-muted">请与收款方确认这个支付链接是否正确。</p>
          </div>
        ) : methods.length === 0 ? (
          <div className="mt-8 rounded-2xl border p-8 text-center">
            <TriangleAlert className="mx-auto size-8 text-destructive" />
            <h1 className="mt-4 text-lg font-semibold tracking-tight">暂无可用的支付方式</h1>
            <p className="mt-2 text-sm leading-6 text-muted">收款方还没有配置支付宝或微信收款。</p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-5 rounded-2xl border p-5 sm:p-7">
            <div className="space-y-2">
              <Label htmlFor="aggregate-money">支付金额（元）</Label>
              <Input
                id="aggregate-money"
                inputMode="decimal"
                required
                value={money}
                onChange={(event) => setMoney(event.target.value.replace(/[^\d.]/g, ""))}
                placeholder="0.00"
                className="h-12 text-lg"
              />
            </div>

            <div className="space-y-2">
              <Label>支付方式</Label>
              <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${Math.min(methods.length, 2)}, minmax(0, 1fr))` }}>
                {methods.map((method) => (
                  <button
                    key={method}
                    type="button"
                    onClick={() => setType(method)}
                    className={cn(
                      "flex items-center justify-center gap-2 rounded-lg border p-3 text-sm font-medium transition-colors",
                      type === method ? "border-primary bg-primary/5 text-primary" : "hover:bg-foreground/[0.025]",
                    )}
                  >
                    <CreditCard className="size-4" />
                    {METHOD_LABEL[method] ?? method}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="aggregate-remark">备注（可选）</Label>
              <Input
                id="aggregate-remark"
                value={remark}
                onChange={(event) => setRemark(event.target.value)}
                placeholder="给收款方看的说明"
                maxLength={60}
              />
            </div>

            {error ? <p className="rounded-lg bg-destructive/[0.06] px-3 py-2.5 text-sm text-destructive">{error}</p> : null}

            <Button type="submit" size="lg" className="w-full" disabled={busy}>
              {busy ? (
                <>
                  <LoaderCircle className="animate-spin" />
                  正在创建订单…
                </>
              ) : (
                "去支付"
              )}
            </Button>

            <p className="flex items-start gap-2 text-xs leading-5 text-muted">
              <ShieldCheck className="mt-0.5 size-3.5 shrink-0 text-success" />
              提交后会跳到收银台完成付款，付款结果页面会实时确认。
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
